import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { BreadcrumbService } from 'xng-breadcrumb';
import { BehaviorSubject, combineLatest, filter, map, Observable } from 'rxjs';
import { TimeZoneService } from '../../../../../../common/time-zone/time-zone.service';
import {
  normalizeAllSelection,
  withAllOption,
  withoutAllOption,
} from '../../../../../../shared/helper/selectionhelper';
import { AttendanceUsersQueryModel } from '../../../../../../shared/store/attendance/user-work-attendance/types';
import { UserWorkAttendanceService } from '../../../../../../shared/store/attendance/user-work-attendance/user-work-attendance.service';
import { GroupService } from '../../../../../../shared/store/group/group.service';
import { ReportingService } from '../../../../../../shared/store/reporting/reporting.service';
import { Period } from '../../../../../../shared/store/reporting/types';
import { ListItem } from '../../../../../../shared/store/typeahead/types';
import { View } from '../../../../../../shared/store/view/types';
import { ViewService } from '../../../../../../shared/store/view/views.service';
import { ATTENDANCE_DEFAULT_GROUP_IDS } from '../../shared/attendance-dashboard.types';

/** Period selected when the screen opens, whatever the entry point. */
const ATTENDANCE_DEFAULT_PERIOD = 'CurrentMonth';

/**
 * Owns the period and the groups of the salesperson dashboard: the period goes out as the query the
 * parent sends, the groups only narrow the picker. The salesperson itself lives in the URL, owned by
 * the parent.
 */
@UntilDestroy()
@Component({
  selector: 'app-sales-person-attendance-dashboard-subheader',
  standalone: false,
  templateUrl: './sales-person-attendance-dashboard-subheader.component.html',
  styleUrl: './sales-person-attendance-dashboard-subheader.component.scss',
})
export class SalesPersonAttendanceDashboardSubheaderComponent
  implements OnInit, OnDestroy
{
  @Input() userId: number | null = null;

  /** Salesperson drawn by the page: kept in the picker even when its list would not offer them. */
  @Input() set displayedUser(value: ListItem | null) {
    this.displayedUser$.next(value);
  }

  @Output() queryChange = new EventEmitter<AttendanceUsersQueryModel>();
  /** null: a group change left no salesperson to offer, the page empties. */
  @Output() salesPersonChange = new EventEmitter<number | null>();

  view$: Observable<View>;
  periods$: Observable<Period[]>;
  groups$: Observable<ListItem[]>;
  salesPersonUsers$: Observable<ListItem[]>;
  users$: Observable<ListItem[]>;

  period = ATTENDANCE_DEFAULT_PERIOD;
  periods: Period[] = [];
  periodGroups: string[] = [];
  groupIds: number[] = [...ATTENDANCE_DEFAULT_GROUP_IDS];

  private readonly displayedUser$ = new BehaviorSubject<ListItem | null>(null);
  /** Raised by a group change: the next list moves the page to its first salesperson if needed. */
  private followFirstUser = false;

  constructor(
    private readonly viewService: ViewService,
    private readonly reportingService: ReportingService,
    private readonly translateService: TranslateService,
    private readonly groupService: GroupService,
    private readonly userWorkAttendanceService: UserWorkAttendanceService,
    private readonly timeZoneService: TimeZoneService,
    private readonly breadcrumbService: BreadcrumbService,
    @Inject(LOCALE_ID) private readonly locale: string,
  ) {
    this.view$ = this.viewService.getViews.value$;
    this.periods$ = this.reportingService.getPeriods.value$.pipe(
      filter((x) => !!x),
    );
    // stream, not instant: translations may land after the list, and the "All" label must follow.
    this.groups$ = combineLatest([
      this.groupService.getListItem.value$.pipe(filter((x) => !!x)),
      this.translateService.stream(
        'ATTENDANCE_LBL_GROUP_ALL',
      ) as Observable<string>,
    ]).pipe(map(([x, allLabel]) => withAllOption(x, allLabel)));
    this.salesPersonUsers$ =
      this.userWorkAttendanceService.getSalesPersonUsers.value$.pipe(
        filter((x) => !!x),
      );
    this.users$ = combineLatest([
      this.salesPersonUsers$,
      this.displayedUser$,
    ]).pipe(
      map(([users, displayed]) => this.withDisplayedUser(users, displayed)),
    );
  }

  ngOnInit(): void {
    this.view$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe((x) => {
        this.breadcrumbService.set('@salesPersonAttendanceDashboard', x.title);
      });

    this.periods$.pipe(untilDestroyed(this)).subscribe((periods) => {
      this.periods = periods;
      this.periodGroups = [...new Set(periods.map((x) => x.group))];
      this.onPeriodChange(this.period);
    });

    // Without an id, the page opens the first salesperson itself.
    this.salesPersonUsers$.pipe(untilDestroyed(this)).subscribe((users) => {
      const follow = this.followFirstUser;

      this.followFirstUser = false;

      // An empty list empties the page rather than leaving the previous salesperson drawn.
      if (follow && this.userId && !users.some((x) => x.id === this.userId))
        this.salesPersonChange.emit(users[0]?.id ?? null);
    });

    this.groupService.getListItem.call();
    this.reportingService.getPeriods.call(new Date());
  }

  ngOnDestroy(): void {
    this.breadcrumbService.set('@salesPersonAttendanceDashboard', '');
    this.userWorkAttendanceService.getSalesPersonUsers.reset();
    this.groupService.getListItem.reset();
    this.reportingService.getPeriods.reset();
  }

  onPeriodChange(value: string): void {
    this.period = value;
    this.emitQuery();
  }

  /** Groups only narrow the picker: the rows answer for the salesperson drawn, whatever their group. */
  onGroupIdsChange(): void {
    this.groupIds = normalizeAllSelection(this.groupIds);
    this.followFirstUser = true;
    this.loadUsers();
  }

  onSalesPersonChange(userId: number | null): void {
    if (!userId) return;

    this.salesPersonChange.emit(userId);
  }

  private emitQuery(): void {
    const query = this.buildQuery();

    if (!query) return;

    // A period change never moves the salesperson, even when a failed group reload left the flag up.
    this.followFirstUser = false;
    this.loadUsers();
    this.queryChange.emit(query);
  }

  /**
   * The picker offers the active salespeople of the groups picked only; the rows answer for any id, a
   * former employee or someone outside the groups opened from the team dashboard.
   */
  private loadUsers(): void {
    const query = this.buildQuery();

    if (!query) return;

    this.userWorkAttendanceService.getSalesPersonUsers.call({
      ...query,
      groupIds: withoutAllOption(this.groupIds),
      includeFormerEmployees: false,
    });
  }

  private buildQuery(): AttendanceUsersQueryModel | null {
    const period = this.periods.find((x) => x.value === this.period);

    if (!period) return null;

    // Bounds come as UTC instants: read them as days of the site's time zone.
    const from = this.timeZoneService.fromUtcToLocal(
      period.startDate,
      'yyyy-MM-dd',
    );
    const end = this.timeZoneService.fromUtcToLocal(
      period.endDate,
      'yyyy-MM-dd',
    );
    const today = this.timeZoneService.fromUtcToLocal(new Date(), 'yyyy-MM-dd');

    return {
      from,
      // A current period runs past today: days not lived yet would be drawn as absences.
      to: end > today ? today : end,
      salesTeamIds: [],
      groupIds: [],
      includeFormerEmployees: true,
    };
  }

  /** The salesperson drawn stays offered, even when the picker would not list them. */
  private withDisplayedUser(
    users: ListItem[],
    displayed: ListItem | null,
  ): ListItem[] {
    if (!displayed || users.some((x) => x.id === displayed.id)) return users;

    // Back order kept: the displayed salesperson is slotted in by name rather than appended.
    const index = users.findIndex(
      (x) =>
        x.name.localeCompare(displayed.name, this.locale, {
          sensitivity: 'base',
        }) > 0,
    );

    return index < 0
      ? [...users, displayed]
      : [...users.slice(0, index), displayed, ...users.slice(index)];
  }
}
